import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Lock } from "lucide-react";

export default function AdminTeam({ currentUserEmail }) {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/team");
      setTeam(data);
    } catch {
      toast.error("Failed to load team");
    }
  };

  useEffect(() => { reload(); }, []);

  const add = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/team", { email: email.trim().toLowerCase() });
      setEmail("");
      toast.success("Admin added — they'll be promoted on next Google sign-in");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (em) => {
    if (!window.confirm(`Remove admin access for ${em}?`)) return;
    try {
      await api.delete(`/admin/team/${encodeURIComponent(em)}`);
      toast.success("Removed");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  // Dedupe by email (defensive: dev data can have multiple user rows per email)
  const activeAdmins = useMemo(
    () => Array.from(new Map((team?.active_admins || []).map((a) => [a.email, a])).values()),
    [team]
  );
  const pendingInvites = useMemo(
    () => (team?.db_allowlist || []).filter((em) => !activeAdmins.find((a) => a.email === em)),
    [team, activeAdmins]
  );

  if (!team) return <div className="text-sm text-[#64748B]">Loading…</div>;

  const envSet = new Set(team.env_allowlist || []);
  const dbSet = new Set(team.db_allowlist || []);

  return (
    <div className="space-y-6" data-testid="team-view">
      <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6">
        <p className="text-xs uppercase tracking-wider text-[#64748B] mb-4">Active admin accounts</p>
        <div className="space-y-2">
          {activeAdmins.map((a) => {
            const isEnvLocked = envSet.has(a.email);
            const isSelf = a.email === (currentUserEmail || "").toLowerCase();
            return (
              <div key={a.email} className="flex items-center justify-between gap-3 bg-[#F7F5F0] rounded-lg px-4 py-3" data-testid={`admin-row-${a.email}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {a.picture ? (
                    <img src={a.picture} alt={a.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#1E3A8A] text-white grid place-items-center font-semibold text-sm">
                      {(a.name || a.email)[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{a.name || a.email}</p>
                    <p className="text-xs text-[#64748B] truncate">{a.email}{isSelf ? " · you" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isEnvLocked && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1"><Lock className="w-3 h-3" />env</Badge>}
                  {!isEnvLocked && !isSelf && (
                    <Button variant="outline" size="sm" onClick={() => remove(a.email)} className="border-[#E2DDD3] hover:bg-red-50 hover:text-red-600" data-testid={`remove-${a.email}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {activeAdmins.length === 0 && <p className="text-sm text-[#64748B]">No active admins yet.</p>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2DDD3] p-6">
        <p className="text-xs uppercase tracking-wider text-[#64748B] mb-4 flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Invite a new admin
        </p>
        <form onSubmit={add} className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="bg-[#F7F5F0] border-[#E2DDD3] h-11"
            data-testid="invite-email-input"
            required
          />
          <Button type="submit" disabled={busy} className="bg-[#FF008F] hover:bg-[#D60079] text-white" data-testid="invite-submit">
            Add admin
          </Button>
        </form>
        <p className="text-xs text-[#64748B] mt-3 flex items-start gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          They&apos;ll get admin access the next time they sign in with this Google email at <code className="text-[#0F172A]">/admin/login</code>.
        </p>
        {pendingInvites.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#E2DDD3]">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Pending invites (not yet signed in)</p>
            <div className="flex flex-wrap gap-2">
              {pendingInvites.map((e) => (
                <Badge key={e} variant="outline" className="bg-[#F7F5F0] border-[#E2DDD3] text-[#0F172A] gap-2">
                  {e}
                  <button onClick={() => remove(e)} className="hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
